import moment from 'moment';
import ApiReports from './apireport';

// ---------------------------------------------------------------------------
// Histórico de uso (tb_user_usage_history / tb_user_usage_daily) — Reports API, api/report/usage.
// A API devolve JSON TODO em minúsculo (LowercaseContractResolver): normalizar aqui, na entrada.
// Sem async/await em arrow (babel do react-scripts 4): tudo em Promise chain.
// ---------------------------------------------------------------------------

const BASE = '/report/usage';

export const fmtDate = (d) => moment(d).format('YYYY-MM-DD');

function query(params) {
  const q = Object.keys(params)
    .filter((k) => params[k] !== undefined && params[k] !== null && params[k] !== '' && params[k] !== 0)
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`);
  return q.length ? `?${q.join('&')}` : '';
}

function unwrap(result) {
  const body = result && result.data;
  if (!body || body.success === false) {
    const msg = body && body.errors && body.errors[0];
    throw new Error(msg || 'Falha ao consultar o histórico de uso.');
  }
  return body;
}

// ─────────────────────────── normalizadores ───────────────────────────

const num = (v) => (v === null || v === undefined ? 0 : Number(v));

export const normalizeRow = (r) => ({
  id: r.id,
  occurredAt: r.occurredat ?? r.occurredAt,
  userId: r.userid ?? r.userId ?? null,
  userName: r.username ?? r.userName ?? '',
  userKind: r.userkind ?? r.userKind,
  api: r.api,
  httpMethod: r.httpmethod ?? r.httpMethod ?? null,
  route: r.route ?? null,
  path: r.path,
  url: r.url ?? null,
  screen: r.screen ?? null,
  screenRoute: r.screenroute ?? r.screenRoute ?? null,
  screenVisitId: r.screenvisitid ?? r.screenVisitId ?? null,
  actionKind: r.actionkind ?? r.actionKind,
  actionGroup: r.actiongroup ?? r.actionGroup,
  entityType: r.entitytype ?? r.entityType ?? null,
  entityId: r.entityid ?? r.entityId ?? null,
  statusCode: r.statuscode ?? r.statusCode ?? null,
  durationMs: r.durationms ?? r.durationMs ?? null,
  ip: r.ip ?? null,
  userAgent: r.useragent ?? r.userAgent ?? null,
  source: r.source,
  details: parseDetails(r.details),
  firstExport: !!(r.firstexport ?? r.firstExport),
  requestId: r.requestid ?? r.requestId ?? null,
});

export function parseDetails(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch (e) { return { raw }; }
}

export const normalizeSummary = (s) => ({
  events: num(s?.events), activeUsers: num(s?.activeusers), screens: num(s?.screens),
  reads: num(s?.reads), writes: num(s?.writes), exports: num(s?.exports),
  denials: num(s?.denials), errors: num(s?.errors), clientErrors: num(s?.clienterrors),
  logins: num(s?.logins), loginFailures: num(s?.loginfailures),
  sumDurationMs: num(s?.sumdurationms), durationSamples: num(s?.durationsamples),
  maxDurationMs: num(s?.maxdurationms), updatedAt: s?.updatedat ?? null,
  avgDurationMs: num(s?.durationsamples) > 0 ? Math.round(num(s?.sumdurationms) / num(s?.durationsamples)) : 0,
});

export const normalizeByDay = (d) => ({
  day: d.day, events: num(d.events), users: num(d.users), screens: num(d.screens),
  denials: num(d.denials), errors: num(d.errors),
  avgDurationMs: num(d.durationsamples) > 0 ? Math.round(num(d.sumdurationms) / num(d.durationsamples)) : 0,
});

export const normalizeTopScreen = (t) => ({
  screenRoute: t.screenroute, visits: num(t.visits), requests: num(t.requests), errors: num(t.errors),
  avgDurationMs: num(t.durationsamples) > 0 ? Math.round(num(t.sumdurationms) / num(t.durationsamples)) : 0,
});

export const normalizeTopAction = (t) => ({
  route: t.route, entityType: t.entitytype ?? '', count: num(t.count), errors: num(t.errors), clientErrors: num(t.clienterrors),
  maxDurationMs: num(t.maxdurationms),
  avgDurationMs: num(t.count) > 0 ? Math.round(num(t.sumdurationms) / num(t.count)) : 0,
});

export const normalizeHeaviest = (h) => ({
  route: h.route, screenRoute: h.screenroute ?? '', count: num(h.count),
  totalDurationMs: num(h.sumdurationms), maxDurationMs: num(h.maxdurationms),
  avgDurationMs: num(h.count) > 0 ? Math.round(num(h.sumdurationms) / num(h.count)) : 0,
});

export const normalizeTopUser = (u) => ({
  userId: num(u.userid), userName: u.username || `#${u.userid}`,
  events: num(u.events), screens: num(u.screens), reads: num(u.reads), writes: num(u.writes),
  exports: num(u.exports), denials: num(u.denials), errors: num(u.errors),
  avgDurationMs: num(u.durationsamples) > 0 ? Math.round(num(u.sumdurationms) / num(u.durationsamples)) : 0,
  ips: u.ips ?? null, // null = período acima de 90 dias (o fato não é lido)
});

export const normalizeByEntity = (e) => ({
  entityType: e.entitytype, reads: num(e.reads), writes: num(e.writes), exports: num(e.exports), total: num(e.total),
});

export const normalizeScreen = (s) => ({
  screenRoute: s.screenroute, visits: num(s.visits), users: num(s.users), requests: num(s.requests),
  sumDurationMs: num(s.sumdurationms), maxDurationMs: num(s.maxdurationms),
  errors: num(s.errors), clientErrors: num(s.clienterrors), denials: num(s.denials), writes: num(s.writes), exports: num(s.exports),
  lastDay: s.lastday ?? null,
  avgDurationMs: num(s.durationsamples) > 0 ? Math.round(num(s.sumdurationms) / num(s.durationsamples)) : 0,
  requestsPerVisit: num(s.visits) > 0 ? Math.round((num(s.requests) / num(s.visits)) * 10) / 10 : 0,
});

export const normalizeVisit = (v) => ({
  screenVisitId: v.screenvisitid, occurredAt: v.occurredat, lastAt: v.lastat ?? null,
  userId: v.userid ?? null, userName: v.username || '',
  screen: v.screen, screenRoute: v.screenroute, url: v.url, ip: v.ip,
  requests: num(v.requests), sumDurationMs: num(v.sumdurationms), denials: num(v.denials),
  errors: num(v.errors), exports: num(v.exports), writes: num(v.writes),
});

export const normalizeWriteEvent = (e) => ({
  id: e.id, occurredAt: e.occurredat, actorId: e.actorid ?? null, actorName: e.actorname || '', actorType: e.actortype || '',
  eventType: e.eventtype || '', eventTypeCode: e.eventtypecode || '', aggregateType: e.aggregatetype || '', aggregateId: e.aggregateid ?? null,
  calledId: e.calledid ?? null, source: e.source || '',
  eventData: parseDetails(e.eventdata), changedFields: parseDetails(e.changedfields), oldValues: parseDetails(e.oldvalues),
});

export const normalizeEntityUser = (u) => ({
  userId: u.userid ?? null, userName: u.username || (u.userid ? `#${u.userid}` : 'anônimo'),
  accesses: num(u.accesses), reads: num(u.reads), writes: num(u.writes), exports: num(u.exports),
  denials: num(u.denials), firstAt: u.firstat, lastAt: u.lastat, ips: u.ips ?? '',
});

// ─────────────────────────── chamadas ───────────────────────────

// Aba "Tráfego por IP" (resumo tb_user_usage_ip_daily).
export const normalizeEndpointRank = (r) => ({
  api: r.api ?? '', route: r.route ?? '', requests: num(r.requests), users: num(r.users), denials: num(r.denials), errors: num(r.errors), clientErrors: num(r.clienterrors),
  avgDurationMs: num(r.avgdurationms), maxDurationMs: num(r.maxdurationms), lastDay: r.lastday ?? null,
});
export const normalizeEndpointCaller = (r) => ({
  userId: num(r.userid), userName: r.username ?? '', ip: r.ip ?? '', ips: r.ips ?? '', ipCount: num(r.ipcount), users: num(r.users), calls: num(r.calls), denials: num(r.denials),
  firstAt: r.firstat ?? null, lastAt: r.lastat ?? null, medianDelta: r.mediandelta ?? null, minDelta: r.mindelta ?? null,
  lt1: num(r.lt1), lt5: num(r.lt5), lt30: num(r.lt30), lt300: num(r.lt300), gt300: num(r.gt300), peakPerMinute: num(r.peakperminute),
});
export const normalizeIpRank = (r) => ({
  ip: r.ip, requests: num(r.requests), endpoints: num(r.endpoints), users: r.users === null || r.users === undefined ? null : num(r.users),
  denials: num(r.denials), errors: num(r.errors), clientErrors: num(r.clienterrors),
  peakHourRequests: num(r.peakhourrequests), peakApi: r.peakapi ?? null, peakRoute: r.peakroute ?? null, peakAt: r.peakat ?? null,
  firstAt: r.firstat ?? null, lastAt: r.lastat ?? null,
});
export const normalizeIpEndpoint = (e) => ({
  api: e.api, route: e.route ?? '', requests: num(e.requests), denials: num(e.denials), errors: num(e.errors), clientErrors: num(e.clienterrors),
  peakHourRequests: num(e.peakhourrequests), peakAt: e.peakat ?? null, firstAt: e.firstat ?? null, lastAt: e.lastat ?? null,
});
export const normalizeIpPoint = (s) => ({ at: s.at, requests: num(s.requests), denials: num(s.denials), errors: num(s.errors) });

const dashFilter = (f) => ({
  startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd),
  api: f.api, userId: f.userId, actionGroup: f.actionGroup, screenRoute: f.screenRoute,
});

// Botão "Atualizar agora" do dashboard (rollup do daily): estado da trava e resultado.
export const normalizeRollup = (d) => ({
  lastRollupAt: d?.lastrollupat ?? null, nextAllowedAt: d?.nextallowedat ?? null,
  retryAfterSeconds: num(d?.retryafterseconds), cooldownMinutes: num(d?.cooldownminutes) || 10, rows: num(d?.rows),
});

// Aba Erros: rota+status agregados (fato) e série diária (rollup).
export const normalizeErrorRoute = (r) => ({
  api: r.api, route: r.route ?? '', statusCode: num(r.statuscode), count: num(r.count),
  firstAt: r.firstat ?? null, lastAt: r.lastat ?? null, users: num(r.users), ips: num(r.ips), anonymous: num(r.anonymous),
  lastRequestId: r.lastrequestid ?? null, lastExceptionType: r.lastexceptiontype ?? null, lastExceptionMessage: r.lastexceptionmessage ?? null,
  lastReason: r.lastreason ?? null,
});
export const normalizeErrorsTotals = (t) => ({ server5xx: num(t?.server5xx), client4xx: num(t?.client4xx), routes5xx: num(t?.routes5xx), routes4xx: num(t?.routes4xx) });
export const normalizeErrorsByDay = (d) => ({ day: d.day, errors: num(d.errors), clientErrors: num(d.clienterrors) });

export const UsageService = {
  summary: (f) => ApiReports.get(`${BASE}/dashboard/summary${query(dashFilter(f))}`).then((r) => {
    const d = unwrap(r).data || {};
    return { current: normalizeSummary(d.current), previous: normalizeSummary(d.previous) };
  }),
  byDay: (f) => ApiReports.get(`${BASE}/dashboard/by-day${query(dashFilter(f))}`).then((r) => (unwrap(r).data || []).map(normalizeByDay)),
  byHour: (f) => ApiReports.get(`${BASE}/dashboard/by-hour${query(dashFilter(f))}`).then((r) => {
    const d = unwrap(r).data || {};
    return {
      byHour: (d.byhour || []).map((h) => ({ hour: num(h.hour), events: num(h.events) })),
      heatmap: (d.heatmap || []).map((h) => ({ weekday: num(h.weekday), hour: num(h.hour), events: num(h.events) })),
    };
  }),
  topScreens: (f, limit) => ApiReports.get(`${BASE}/dashboard/top-screens${query({ ...dashFilter(f), screenRoute: undefined, limit })}`).then((r) => (unwrap(r).data || []).map(normalizeTopScreen)),
  topActions: (f, limit) => ApiReports.get(`${BASE}/dashboard/top-actions${query({ ...dashFilter(f), limit })}`).then((r) => (unwrap(r).data || []).map(normalizeTopAction)),
  heaviest: (f, orderBy, groupByScreen, limit) => ApiReports.get(`${BASE}/dashboard/heaviest${query({ ...dashFilter(f), actionGroup: undefined, orderBy, groupByScreen: groupByScreen ? 'true' : undefined, limit })}`).then((r) => (unwrap(r).data || []).map(normalizeHeaviest)),
  // orderBy: coluna do ranking (events | screens | reads | writes | exports | denials | errors | avg) — ordem e corte no servidor.
  topUsers: (f, limit, orderBy) => ApiReports.get(`${BASE}/dashboard/top-users${query({ ...dashFilter(f), userId: undefined, limit, orderBy: orderBy || undefined })}`).then((r) => (unwrap(r).data || []).map(normalizeTopUser)),
  byEntity: (f, limit) => ApiReports.get(`${BASE}/dashboard/by-entity${query({ ...dashFilter(f), actionGroup: undefined, limit })}`).then((r) => (unwrap(r).data || []).map(normalizeByEntity)),
  denials: (f, limit) => ApiReports.get(`${BASE}/dashboard/denials${query({ startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), api: f.api, userId: f.userId, limit })}`).then((r) => {
    const d = unwrap(r).data || {};
    return {
      byKind: (d.bykind || []).map((k) => ({ actionKind: k.actionkind, count: num(k.count) })),
      byDay: (d.byday || []).map((k) => ({ day: k.day, actionKind: k.actionkind, count: num(k.count) })),
      byUser: (d.byuser || []).map(normalizeTopUser),
    };
  }),
  byApi: (f) => ApiReports.get(`${BASE}/dashboard/by-api${query({ startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), userId: f.userId, actionGroup: f.actionGroup })}`).then((r) => (unwrap(r).data || []).map((a) => ({ api: a.api, events: num(a.events) }))),

  visits: (userId, dateStart, dateEnd, page, pageSize, ip) => ApiReports.get(`${BASE}/user-activity/visits${query({ userId, startDate: fmtDate(dateStart), endDate: fmtDate(dateEnd), page, pageSize, ip })}`).then((r) => {
    const b = unwrap(r);
    return { data: (b.data || []).map(normalizeVisit), count: num(b.count) };
  }),
  visitRows: (screenVisitId) => ApiReports.get(`${BASE}/user-activity/visit/${screenVisitId}`).then((r) => (unwrap(r).data || []).map(normalizeRow)),
  // Exceções gravadas pelo middleware para o request_id de uma linha (tb_exception_logs).
  exceptionByRequest: (requestId, limit) => ApiReports.get(`${BASE}/exception/${encodeURIComponent(requestId)}${query({ limit })}`).then((r) => (unwrap(r).data || []).map((e) => ({
    id: e.id, occurredAt: e.occurredat, exceptionType: e.exceptiontype || '', message: e.message || '', stackTrace: e.stacktrace || '',
    innerExceptionMessage: e.innerexceptionmessage || '', requestPath: e.requestpath || '', requestMethod: e.requestmethod || '', statusCode: e.statuscode ?? null, machineName: e.machinename || '',
  }))),
  // Falhas de login por IP e por e-mail (fato, ≤ 90 dias).
  authFailures: (f, limit) => ApiReports.get(`${BASE}/auth/failures${query({ startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), limit, ip: f.ip })}`).then((r) => {
    const d = unwrap(r).data || {};
    const norm = (x) => ({ key: x.key || '—', failures: num(x.failures), distinct: num(x.distinct), lastAt: x.lastat });
    return { total: num(d.total), byIp: (d.byip || []).map(norm), byEmail: (d.byemail || []).map(norm) };
  }),
  // Aba "Telas": listagem (rollup, paginada, busca) e visitas de uma tela (fato, ≤ 90 dias).
  screens: (f, search, page, pageSize) => ApiReports.get(`${BASE}/dashboard/screens${query({ ...dashFilter(f), screenRoute: undefined, search: search || undefined, page, pageSize })}`).then((r) => {
    const b = unwrap(r);
    return { data: (b.data || []).map(normalizeScreen), count: num(b.count) };
  }),
  screenVisits: (screenRoute, dateStart, dateEnd, page, pageSize) => ApiReports.get(`${BASE}/user-activity/screen-visits${query({ screenRoute, startDate: fmtDate(dateStart), endDate: fmtDate(dateEnd), page, pageSize })}`).then((r) => {
    const b = unwrap(r);
    return { data: (b.data || []).map(normalizeVisit), count: num(b.count) };
  }),
  activity: (p) => ApiReports.get(`${BASE}/user-activity/paginate${query({
    userId: p.userId, startDate: fmtDate(p.dateStart), endDate: fmtDate(p.dateEnd), page: p.page, pageSize: p.pageSize,
    actionKind: p.actionKind, actionGroup: p.actionGroup, entityType: p.entityType, entityId: p.entityId, ip: p.ip, api: p.api, route: p.route, routeLike: p.routeLike,
    status: p.status, origin: p.origin, userKind: p.userKind, method: p.method, client: p.client, forwarded: p.forwarded ? true : undefined,
  })}`).then((r) => { const b = unwrap(r); return { data: (b.data || []).map(normalizeRow), count: num(b.count) }; }),
  blocked: (p) => ApiReports.get(`${BASE}/blocked/paginate${query({ startDate: fmtDate(p.dateStart), endDate: fmtDate(p.dateEnd), page: p.page, pageSize: p.pageSize, userId: p.userId, kind: p.kind || undefined, ip: p.ip, origin: p.origin || undefined })}`)
    .then((r) => { const b = unwrap(r); return { data: (b.data || []).map(normalizeRow), count: num(b.count) }; }),
  // Trilha de ESCRITA do outbox (live ∪ archive) da entidade: o que foi feito, por quem, de-para.
  entityWrites: (entityType, entityId, dateStart, dateEnd, page, pageSize) => ApiReports.get(`${BASE}/entity-access/writes${query({ entityType, entityId, startDate: fmtDate(dateStart), endDate: fmtDate(dateEnd), page, pageSize })}`).then((r) => {
    const b = unwrap(r);
    return { data: (b.data || []).map(normalizeWriteEvent), count: num(b.count) };
  }),
  entityAccess: (entityType, entityId, dateStart, dateEnd, limit, ip) => ApiReports.get(`${BASE}/entity-access${query({ entityType, entityId, startDate: fmtDate(dateStart), endDate: fmtDate(dateEnd), limit, ip })}`).then((r) => {
    const d = unwrap(r).data || {};
    return { users: (d.users || []).map(normalizeEntityUser), events: (d.events || []).map(normalizeRow) };
  }),
  slow: (dateStart, dateEnd, minMs, limit) => ApiReports.get(`${BASE}/slow${query({ startDate: fmtDate(dateStart), endDate: fmtDate(dateEnd), minMs, limit })}`).then((r) => (unwrap(r).data || []).map(normalizeRow)),
  // Rollup manual do daily. O POST responde success=false DENTRO do cooldown, com data (nextAllowedAt):
  // não passa pelo unwrap, para o botão assumir a contagem em vez de mostrar erro.
  rollupStatus: () => ApiReports.get(`${BASE}/dashboard/rollup/status`).then((r) => ({ ok: true, ...normalizeRollup(unwrap(r).data) })),
  rollup: () => ApiReports.post(`${BASE}/dashboard/rollup`).then((r) => {
    const b = (r && r.data) || {};
    if (!b.data && b.success === false) throw new Error((b.errors && b.errors[0]) || 'Falha ao consolidar o histórico de uso.');
    return { ok: b.success !== false, error: b.errors && b.errors[0], ...normalizeRollup(b.data) };
  }),
  // Aba Erros. summary/occurrences leem o fato (até 90 dias); by-day lê o rollup (até 400 dias).
  errorsSummary: (f, kind, limit) => ApiReports.get(`${BASE}/errors/summary${query({ startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), api: f.api, kind, limit, ip: f.ip })}`).then((r) => {
    const d = unwrap(r).data || {};
    return { totals: normalizeErrorsTotals(d.totals), routes: (d.routes || []).map(normalizeErrorRoute) };
  }),
  errorOccurrences: (f, route, statusCode, page, pageSize) => ApiReports.get(`${BASE}/errors/occurrences${query({ startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), api: f.api, route, statusCode, page, pageSize, ip: f.ip })}`).then((r) => {
    const b = unwrap(r);
    return { data: (b.data || []).map((x) => ({ ...normalizeRow(x), exceptionType: x.exceptiontype ?? null, exceptionMessage: x.exceptionmessage ?? null })), count: num(b.count) };
  }),
  errorsByDay: (f) => ApiReports.get(`${BASE}/errors/by-day${query({ startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), api: f.api })}`).then((r) => (unwrap(r).data || []).map(normalizeErrorsByDay)),
  // Aba "Tráfego por IP" (resumo por dia/hora/IP/API/rota, até 400 dias). threshold = requests numa hora num endpoint.
  ipRanking: (f, threshold, minRequests, page, pageSize) => ApiReports.get(`${BASE}/ip-traffic/ranking${query({ startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), api: f.api, ip: f.ip, threshold, minRequests, page, pageSize })}`).then((r) => {
    const b = unwrap(r); const tt = b.totals || {};
    return { data: (b.data || []).map(normalizeIpRank), count: num(b.count), totals: { ips: num(tt.ips), requests: num(tt.requests), suspicious: num(tt.suspicious) } };
  }),
  ipEndpoints: (ip, f) => ApiReports.get(`${BASE}/ip-traffic/endpoints${query({ ip, startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), api: f.api })}`).then((r) => (unwrap(r).data || []).map(normalizeIpEndpoint)),
  ipSeries: (ip, f, route, granularity) => ApiReports.get(`${BASE}/ip-traffic/series${query({ ip, startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), api: f.api, route: route || undefined, granularity })}`).then((r) => (unwrap(r).data || []).map(normalizeIpPoint)),
  // Rankings do MESMO recorte da linha do tempo (atalhos): usuários, IPs e rotas com mais eventos.
  activityRankings: (p) => ApiReports.get(`${BASE}/user-activity/rankings${query({
    userId: p.userId, startDate: fmtDate(p.dateStart), endDate: fmtDate(p.dateEnd), limit: p.limit || 10,
    actionKind: p.actionKind, actionGroup: p.actionGroup, entityType: p.entityType, entityId: p.entityId, ip: p.ip, api: p.api, route: p.route, routeLike: p.routeLike,
    status: p.status, origin: p.origin, userKind: p.userKind, method: p.method, client: p.client, forwarded: p.forwarded ? true : undefined,
  })}`).then((r) => { const d = unwrap(r).data || {}; const n = (l) => (l || []).map((x) => ({ key: x.key ?? '', label: x.label ?? '', total: num(x.total), failures: num(x.failures) })); return { byUser: n(d.byuser), byIp: n(d.byip), byRoute: n(d.byroute) }; }),
  // Aba Endpoints: ranking pelo resumo diário (até 400 dias); quem chamou / de onde com cadência pelo fato (≤ 90 dias).
  endpointsRanking: (f, routeLike, actionKind, page, pageSize) => ApiReports.get(`${BASE}/endpoints/ranking${query({ startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), api: f.api, routeLike: routeLike || undefined, actionKind: actionKind || undefined, page, pageSize, userId: f.userId || undefined, ip: f.ip || undefined })}`).then((r) => {
    const b = unwrap(r);
    return { data: (b.data || []).map(normalizeEndpointRank), count: num(b.count) };
  }),
  endpointCallers: (route, api, actionKind, f, limit) => ApiReports.get(`${BASE}/endpoints/callers${query({ route, api: api || undefined, actionKind: actionKind || undefined, startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), limit, userId: f.userId || undefined, ip: f.ip || undefined })}`).then((r) => {
    const d = unwrap(r).data || {};
    return { byUser: (d.byuser || []).map(normalizeEndpointCaller), byIp: (d.byip || []).map(normalizeEndpointCaller) };
  }),
  // Aba Logins e sessões (redes conhecidas: tb_user_usage_known_network) e bloco Origem adulterada do Tráfego por IP.
  sessionsSummary: (f) => ApiReports.get(`${BASE}/sessions/summary${query({ startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), userId: f.userId })}`).then((r) => {
    const d = unwrap(r).data || {};
    return { available: (d.available ?? true) !== false, loginsOk: num(d.loginsok), loginsFailed: num(d.loginsfailed), distinctUsers: num(d.distinctusers), newNetworks: num(d.newnetworks) };
  }),
  sessionsNewNetworks: (f, userKind, page, pageSize) => ApiReports.get(`${BASE}/sessions/new-networks${query({ startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), userKind, userId: f.userId, page, pageSize })}`).then((r) => {
    const b = unwrap(r);
    return {
      available: (b.available ?? true) !== false, count: num(b.count),
      data: (b.data || []).map((x) => ({ userId: num(x.userid), userName: x.username ?? '', userKind: x.userkind ?? 'user', network: x.network ?? '', sampleIp: x.sampleip ?? '', firstSeen: x.firstseen, lastSeen: x.lastseen, firstLoginAt: x.firstloginat ?? null, knownNetworks: num(x.knownnetworks), failuresSameDay: num(x.failuressameday) })),
    };
  }),
  sessionsUserNetworks: (f) => ApiReports.get(`${BASE}/sessions/user-networks${query({ userId: f.userId, startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd) })}`)
    .then((r) => (unwrap(r).data || []).map((x) => ({ userKind: x.userkind ?? 'user', network: x.network ?? '', sampleIp: x.sampleip ?? '', firstSeen: x.firstseen, lastSeen: x.lastseen, firstLoginAt: x.firstloginat ?? null, lastLoginAt: x.lastloginat ?? null, isNew: !!x.isnew }))),
  sessionsIntegrations: (f, limit) => ApiReports.get(`${BASE}/sessions/integrations${query({ startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), limit })}`)
    .then((r) => (unwrap(r).data || []).map((x) => ({ userId: num(x.userid), userName: x.username ?? '', networks: num(x.networks), newNetworks: num(x.newnetworks), firstSeen: x.firstseen, lastSeen: x.lastseen, lastIp: x.lastip ?? '', calls: num(x.calls), errors: num(x.errors), topRoute: x.toproute ?? '' }))),
  // Aba Recusas: IPs com mais recusas (tráfego anônimo não tem usuário; o IP é o único eixo). Resumo por IP.
  deniedByIp: (f, limit) => ApiReports.get(`${BASE}/blocked/by-ip${query({ startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), ip: f.ip, limit })}`)
    .then((r) => (unwrap(r).data || []).map((x) => ({ ip: x.ip ?? '', denials: num(x.denials), requests: num(x.requests), routes: num(x.routes), topApi: x.topapi ?? '', topRoute: x.toproute ?? '', topRouteDenials: num(x.toproutedenials) }))),
  ipForwarded: (f, limit) => ApiReports.get(`${BASE}/ip-traffic/forwarded${query({ startDate: fmtDate(f.dateStart), endDate: fmtDate(f.dateEnd), ip: f.ip, limit })}`)
    .then((r) => (unwrap(r).data || []).map((x) => ({ ip: x.ip ?? '', userId: num(x.userid), userName: x.username ?? '', total: num(x.total), firstAt: x.firstat, lastAt: x.lastat, sampleChain: x.samplechain ?? '' }))),
  exportActivity: (payload) => ApiReports.post(`${BASE}/user-activity/excell/new`, payload).then((r) => r.data),
};

export default UsageService;
